import {
  abstract,
  arbitrum,
  avalanche,
  base,
  bsc,
  hyperEvm,
  ink,
  linea,
  mainnet,
  mantle,
  monad,
  optimism,
  plasma,
  polygon,
  scroll,
  unichain,
} from 'viem/chains';

import { arc, robinhoodChain } from '@/constants/chain';

import type { SupportedChainId } from './types';

export const FORWARDING_MULTICALL_ABI = [
  {
    type: 'receive',
    stateMutability: 'payable',
  },
  {
    type: 'function',
    name: 'multicall',
    inputs: [
      {
        name: 'calls',
        type: 'tuple[]',
        internalType: 'struct IMultiCall.Call[]',
        components: [
          {
            name: 'target',
            type: 'address',
            internalType: 'address',
          },
          {
            name: 'revertPolicy',
            type: 'uint8',
            internalType: 'enum IMultiCall.RevertPolicy',
          },
          {
            name: 'value',
            type: 'uint256',
            internalType: 'uint256',
          },
          {
            name: 'data',
            type: 'bytes',
            internalType: 'bytes',
          },
        ],
      },
      {
        name: 'contextdepth',
        type: 'uint256',
        internalType: 'uint256',
      },
    ],
    outputs: [
      {
        name: '',
        type: 'tuple[]',
        internalType: 'struct IMultiCall.Result[]',
        components: [
          {
            name: 'success',
            type: 'bool',
            internalType: 'bool',
          },
          {
            name: 'data',
            type: 'bytes',
            internalType: 'bytes',
          },
        ],
      },
    ],
    stateMutability: 'payable',
  },
];

export const SETTLER_META_TXN_ABI = [
  {
    inputs: [
      {
        components: [
          { internalType: 'address', name: 'recipient', type: 'address' },
          {
            internalType: 'contract IERC20',
            name: 'buyToken',
            type: 'address',
          },
          { internalType: 'uint256', name: 'minAmountOut', type: 'uint256' },
        ],
        internalType: 'struct SettlerBase.AllowedSlippage',
        name: 'slippage',
        type: 'tuple',
      },
      { internalType: 'bytes[]', name: 'actions', type: 'bytes[]' },
      { internalType: 'bytes32', name: '', type: 'bytes32' },
      { internalType: 'address', name: 'msgSender', type: 'address' },
      { internalType: 'bytes', name: 'sig', type: 'bytes' },
    ],
    name: 'executeMetaTxn',
    outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
    stateMutability: 'nonpayable',
    type: 'function',
  },
] as const;

export const ALLOWANCE_HOLDER_EXEC_ABI = [
  {
    type: 'function',
    name: 'exec',
    inputs: [
      { name: 'operator', type: 'address' },
      { name: 'token', type: 'address' },
      { name: 'amount', type: 'uint256' },
      { name: 'target', type: 'address' },
      { name: 'data', type: 'bytes' },
    ],
    outputs: [{ name: 'result', type: 'bytes' }],
    stateMutability: 'payable',
  },
] as const;

export const SETTLER_EXECUTE_ABI = [
  {
    type: 'function',
    name: 'execute',
    inputs: [
      {
        name: 'slippage',
        type: 'tuple',
        components: [
          { name: 'recipient', type: 'address' },
          { name: 'buyToken', type: 'address' },
          { name: 'minAmountOut', type: 'uint256' },
        ],
      },
      { name: 'actions', type: 'bytes[]' },
      { name: 'zid', type: 'bytes32' },
    ],
    outputs: [{ type: 'bool' }],
    stateMutability: 'payable',
  },
] as const;

export const FUNCTION_SELECTORS = {
  ALLOWANCE_HOLDER_EXEC: '0x2213bc0b',
  EXECUTE_META_TXN: '0xfd3ad6d4',
  SETTLER_EXECUTE: '0x1fff991f',
};

// Chains on which `parseSwap` is validated. Deliberately kept as an explicit list
// (a subset of matcha's traded chains) rather than derived from SUPPORTED_CHAINS,
// so enabling trading on a new chain never silently implies parse support for it.
export const PARSE_SUPPORTED_CHAINS = [
  bsc,
  base,
  linea,
  scroll,
  mantle,
  plasma,
  mainnet,
  polygon,
  arbitrum,
  unichain,
  optimism,
  avalanche,
  monad,
  abstract,
  hyperEvm,
  robinhoodChain,
  ink,
  arc,
];

export const NATIVE_SYMBOL_BY_CHAIN_ID: { [key in SupportedChainId]: string } = {
  [bsc.id]: bsc.nativeCurrency.symbol,
  [base.id]: base.nativeCurrency.symbol,
  [linea.id]: linea.nativeCurrency.symbol,
  [scroll.id]: scroll.nativeCurrency.symbol,
  [mantle.id]: mantle.nativeCurrency.symbol,
  [monad.id]: monad.nativeCurrency.symbol,
  [plasma.id]: plasma.nativeCurrency.symbol,
  [mainnet.id]: mainnet.nativeCurrency.symbol,
  [polygon.id]: polygon.nativeCurrency.symbol,
  [unichain.id]: unichain.nativeCurrency.symbol,
  [optimism.id]: optimism.nativeCurrency.symbol,
  [arbitrum.id]: arbitrum.nativeCurrency.symbol,
  [avalanche.id]: avalanche.nativeCurrency.symbol,
  [abstract.id]: abstract.nativeCurrency.symbol,
  [hyperEvm.id]: hyperEvm.nativeCurrency.symbol,
  [robinhoodChain.id]: robinhoodChain.nativeCurrency.symbol,
  [ink.id]: ink.nativeCurrency.symbol,
  [arc.id]: arc.nativeCurrency.symbol,
};

export const NATIVE_TOKEN_ADDRESS = '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE';

export const FORWARDING_MULTICALL_ADDRESS = '0x00000000000000cf9e3c5a26621af382fa17f24f';
export const MULTICALL3_ADDRESS = '0xcA11bde05977b3631167028862bE2a173976CA11';
export const ALLOWANCE_HOLDER_ADDRESS = '0x0000000000001fF3684f28c67538d4D072C22734';

export const ERC_4337_ENTRY_POINT = '0x5FF137D4b0FDCD49DcA30c7CF57E578a026d2789';
